import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { salesApi } from '../../../../config/apiClient';

// ============================================================================
// INTERFACES
// ============================================================================

interface PhoneCall {
  id: number;
  phone: string;
  customerName?: string;
  accountId?: number;
  time: string;
  isNew: boolean;
  isAnswered?: boolean;
  callDuration?: number; // saniye cinsinden
}

interface AllCallsModalProps {
  open: boolean;
  onClose: () => void;
  packageScreenId: number;
  onSelectCall?: (call: PhoneCall) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

const AllCallsModal: React.FC<AllCallsModalProps> = ({
  open,
  onClose,
  packageScreenId,
  onSelectCall,
}) => {
  const { colors: themeColors } = useThemeContext();
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId;

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calls, setCalls] = useState<PhoneCall[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // ============================================================================
  // FUNCTIONS
  // ============================================================================

  const loadTodayCalls = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    setError(null);
    
    console.log('📞 [AllCallsModal] Aramalar yükleniyor...', { companyId, packageScreenId });
    
    try {
      const response = await salesApi.get(
        `/api/PhoneCall/today?packageScreenId=${packageScreenId || ''}`
      );
      
      const calls = response.data;
      console.log('📞 [AllCallsModal] API yanıtı:', calls);
      
      // API direkt array dönüyor (PackageOrderScreen ile aynı format)
      // ✅ API hem camelCase hem PascalCase dönebilir - ikisini de destekle
      const formattedCalls: PhoneCall[] = (Array.isArray(calls) ? calls : []).map((c: any) => {
        // Property isimlerini al (camelCase veya PascalCase)
        const callId = c.id || c.Id;
        const callerPhone = c.callerPhone || c.CallerPhone;
        const callerName = c.callerName || c.CallerName; // Kayıt anında kaydedilen isim
        const callTime = c.callTime || c.CallTime;
        const customerName = c.customerName || c.CustomerName || callerName; // CallerName veya Account'tan
        const isAnswered = c.isAnswered ?? c.IsAnswered ?? false;
        const accountId = c.accountId || c.AccountId;
        const callDuration = c.callDuration || c.CallDuration || 0;
        
        // ✅ Tarih için null kontrolü - Invalid Date hatası önleme
        let callTimeStr = '--:--';
        if (callTime) {
          const timeStr = typeof callTime === 'string' && !callTime.includes('Z') && !callTime.includes('+') ? callTime + 'Z' : callTime;
          const callDate = new Date(timeStr);
          if (!isNaN(callDate.getTime())) {
            callTimeStr = callDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' });
          }
        }
        
        return {
          id: callId,
          phone: callerPhone || 'Bilinmiyor',
          customerName: customerName,
          accountId: accountId,
          time: callTimeStr,
          isNew: !isAnswered, // Cevapsız ise yeni (kırmızı) göster
          isAnswered: isAnswered,
          callDuration: callDuration,
        };
      });
      
      // En yeni aramalar üstte
      formattedCalls.sort((a, b) => b.id - a.id);
      
      console.log('📞 [AllCallsModal] Formatlanan aramalar:', formattedCalls.length);
      setCalls(formattedCalls);
    } catch (err) {
      console.error('📞 [AllCallsModal] Aramalar yüklenirken hata:', err);
      setError('Aramalar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [companyId, packageScreenId]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (open && packageScreenId) {
      loadTodayCalls();
    }
  }, [open, packageScreenId, loadTodayCalls]);

  // ============================================================================
  // FILTERED CALLS
  // ============================================================================

  const filteredCalls = calls.filter((call) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      call.phone.toLowerCase().includes(query) ||
      (call.customerName && call.customerName.toLowerCase().includes(query))
    );
  });

  // İstatistikler
  const totalCalls = calls.length;
  const answeredCalls = calls.filter(c => c.isAnswered).length;
  const missedCalls = calls.filter(c => !c.isAnswered).length;

  // Süreyi formatla
  const formatDuration = (seconds: number): string => {
    if (!seconds || seconds <= 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}dk ${secs}sn`;
    }
    return `${secs}sn`;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: themeColors.primary,
          color: 'white',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bugünün Aramaları
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Bilgilendirme */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: `${themeColors.primary}08`,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon sx={{ fontSize: 14, color: themeColors.primary }} />
            Müşteri seçmek için satıra veya sağdaki butona tıklayın
          </Typography>
        </Box>

        {/* İstatistikler */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Chip
            icon={<PhoneIcon sx={{ fontSize: 16 }} />}
            label={`Toplam: ${totalCalls}`}
            size="small"
            sx={{ bgcolor: 'white' }}
          />
          <Chip
            icon={<PhoneCallbackIcon sx={{ fontSize: 16, color: 'success.main' }} />}
            label={`Cevaplanan: ${answeredCalls}`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<PhoneMissedIcon sx={{ fontSize: 16, color: 'error.main' }} />}
            label={`Cevapsız: ${missedCalls}`}
            size="small"
            color="error"
            variant="outlined"
          />
        </Box>

        {/* Arama */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Telefon veya müşteri adı ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Liste */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : filteredCalls.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchQuery ? 'Arama sonucu bulunamadı' : 'Bugün henüz arama yok'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: '400px', overflow: 'auto' }}>
            {filteredCalls.map((call, index) => (
              <React.Fragment key={call.id}>
                <ListItem
                  button
                  onClick={() => {
                    if (onSelectCall) {
                      onSelectCall(call);
                      onClose();
                    }
                  }}
                  sx={{
                    py: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: `${themeColors.primary}15`,
                      transform: 'scale(1.01)',
                    },
                    '&:active': {
                      bgcolor: `${themeColors.primary}25`,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {call.isAnswered ? (
                      <PhoneCallbackIcon sx={{ color: 'success.main' }} />
                    ) : (
                      <PhoneMissedIcon sx={{ color: 'error.main' }} />
                    )}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                        >
                          {call.phone}
                        </Typography>
                        {call.isNew && (
                          <Chip
                            label="Cevapsız"
                            size="small"
                            color="error"
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                        {call.customerName && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 14, color: 'grey.500' }} />
                            <Typography variant="caption" color="text.secondary">
                              {call.customerName}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 14, color: 'grey.500' }} />
                          <Typography variant="caption" color="text.secondary">
                            {call.time}
                          </Typography>
                        </Box>
                        {call.isAnswered && call.callDuration && call.callDuration > 0 && (
                          <Tooltip title="Görüşme Süresi">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimerIcon sx={{ fontSize: 14, color: 'grey.500' }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatDuration(call.callDuration)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <Tooltip title="Müşteriyi Seç">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onSelectCall) {
                            onSelectCall(call);
                            onClose();
                          }
                        }}
                        sx={{
                          color: themeColors.primary,
                          bgcolor: `${themeColors.primary}10`,
                          '&:hover': {
                            bgcolor: `${themeColors.primary}20`,
                          },
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < filteredCalls.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AllCallsModal;
